import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog_VM, DialogFrame } from "./dialog-base";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { Organization } from "../../../core/models/entities/Organization";
import { useOrganizationRepository } from "../../../data/repositories/OrganizationRepository";
import { CustomAutoComplete } from "../Input/CustomAutoComplete";
import { InputWrapper } from "../Input/InputWrapper";
import { InputLabel } from "../Input/InputLabel";

interface AssignCustomerDialogProps {
  dialogOption: Dialog_VM;
  onConfirm: (customerId: number) => void;
  isLoading?: boolean;
}

export function AssignCustomerDialog({
  dialogOption,
  onConfirm,
  isLoading,
}: AssignCustomerDialogProps) {
  const { t } = useTranslation();
  const [customer, setCustomer] = useState<Organization | null>(null);
  const [customers, setCustomers] = useState<Organization[]>([]);
  const repo = useOrganizationRepository();

  useEffect(() => {
    if (dialogOption.isOpen) {
      repo.getAllCustomers().then(setCustomers);
      setCustomer(null);
    }
  }, [dialogOption.isOpen]);

  const handleConfirm = () => {
    if (customer) {
      onConfirm(customer.id);
    }
  };

  return (
    <DialogFrame dialogOption={dialogOption}>
      <div className="flex flex-col space-y-4 p-2 min-w-[450px]">
        <p className="text-left text-base font-bold">
          {t("compDialogsInput.assignCustomer")}
        </p>
        <p className="text-sm text-gray-600">
          {t("compDialogsInput.selectCustomerToAssign")}
        </p>

        <InputWrapper>
          <InputLabel>{t("customer")}</InputLabel>
          <CustomAutoComplete<Organization>
            options={customers}
            getOptionKey={(org) => org.id}
            displayOption={(org) => org.name}
            onChange={(org) => setCustomer(org)}
            value={customer}
            placeholder={t("compDialogsInput.searchForCustomer")}
          />
        </InputWrapper>

        <div className="flex justify-end space-x-2 mt-4">
          <SecondaryButton
            onClick={dialogOption.closeDialog}
            disabled={isLoading}
          >
            {t("cancel")}
          </SecondaryButton>
          <PrimaryButton
            onClick={handleConfirm}
            disabled={!customer || isLoading}
          >
            {isLoading
              ? t("compDialogsInput.assigning")
              : t("compDialogsInput.assign")}
          </PrimaryButton>
        </div>
      </div>
    </DialogFrame>
  );
}
